import { HttpParams } from '@angular/common/http';
import { ChangeDetectionStrategy, Component, DestroyRef, OnInit, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldAppearance, MatFormFieldModule, SubscriptSizing } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatAutocompleteModule, MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { FormArray, FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatDividerModule } from '@angular/material/divider';
import { MatCardAppearance, MatCardModule } from '@angular/material/card';
import { PricePipe } from '@/common/pipes/price.pipe';
import { OfferService, Slot, SlotService } from '../../../../../reservation.model';
import { ReservationSummaryApiService } from '../../../../services/reservation-summary-api.service';
import { ReservationSummaryStoreService } from '../../../../services/reservation-summary-store.service';
import { InfoboxService } from '@/common/infobox/infobox.service';
import { LoaderService } from '@/common/loader/loader.service';
import { filter, map, startWith, take } from 'rxjs';
import {
	getReservationSlotServicesSum,
	recalculateReservationSlotServicesPrices,
} from './_utils/reservation-slot-service-pricing.util';
import { findServiceByName, getAutocompleteServices } from './_utils/reservation-services-autocomplete.util';
import { RESERVATION_SERVICES_DIALOG_MESSAGES } from './reservation-services-dialog.messages';
import { ReservationServicesDialogParticipantComponent } from './reservation-services-dialog-participant/reservation-services-dialog-participant.component';

interface ReservationServicesDialogData {
	slots: Slot[];
	params: HttpParams;
	nightsStay?: number;
	offerTotalPrice: number;
}

@Component({
	selector: 'app-reservation-services-dialog',
	standalone: true,
	imports: [
		MatDialogModule,
		MatButtonModule,
		MatFormFieldModule,
		MatInputModule,
		MatAutocompleteModule,
		ReactiveFormsModule,
		MatDividerModule,
		MatCardModule,
		PricePipe,
		ReservationServicesDialogParticipantComponent,
	],
	templateUrl: './reservation-services-dialog.component.html',
	styleUrls: ['./reservation-services-dialog.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ReservationServicesDialogComponent implements OnInit {
	private static readonly EXCLUDED_SERVICE_TYPE_ID = 13;

	readonly data = inject<ReservationServicesDialogData>(MAT_DIALOG_DATA);
	readonly sectionsAppearance: MatCardAppearance = 'outlined';
	readonly formFieldAppearance: MatFormFieldAppearance = this.sectionsAppearance === 'outlined' ? 'outline' : 'fill';
	readonly formFieldSubscriptSizing: SubscriptSizing = 'dynamic';
	readonly globalServiceControl = new FormControl<string>('', { nonNullable: true });
	readonly participantServiceControls = new FormArray<FormControl<string>>(
		this.data.slots.map(() => new FormControl<string>('', { nonNullable: true })),
	);
	readonly globalSearchQuery = toSignal(
		this.globalServiceControl.valueChanges.pipe(startWith(this.globalServiceControl.value)),
		{ initialValue: '' },
	);
	readonly slots = signal<Slot[]>(
		this.data.slots.map((slot) => ({
			...slot,
			slotServices: [...slot.slotServices],
			priceAdjustment: slot.priceAdjustment ?? 0,
		})),
	);
	readonly selectedGlobalService = signal<string | null>(null);
	readonly globalAutocompleteOptions = computed(() =>
		getAutocompleteServices({
			allServices: this.allServices(),
			slots: this.slots(),
			mode: 'not-on-all-slots',
			searchQuery: this.globalSearchQuery() ?? '',
		}),
	);
	readonly selectedServicesTotal = computed(() =>
		this.slots().reduce((overallAccumulator, slot) => overallAccumulator + getReservationSlotServicesSum(slot), 0),
	);
	readonly initialServicesTotal = this.data.slots.reduce(
		(overallAccumulator, slot) => overallAccumulator + getReservationSlotServicesSum(slot),
		0,
	);
	readonly initialPriceAdjustmentsTotal = this.data.slots.reduce(
		(overallAccumulator, slot) => overallAccumulator + (slot.priceAdjustment ?? 0),
		0,
	);
	readonly selectedPriceAdjustmentsTotal = computed(() =>
		this.slots().reduce((overallAccumulator, slot) => overallAccumulator + (slot.priceAdjustment ?? 0), 0),
	);
	readonly priceDifference = computed(() => this.selectedServicesTotal() - this.initialServicesTotal);
	readonly priceAdjustmentDifference = computed(
		() => this.selectedPriceAdjustmentsTotal() - this.initialPriceAdjustmentsTotal,
	);
	readonly offerTotalPrice = computed(
		() => this.data.offerTotalPrice + this.priceDifference() + this.priceAdjustmentDifference(),
	);
	readonly isSaving = signal(false);
	readonly reservationId = toSignal(
		inject(ReservationSummaryStoreService)
			.getters()
			.reservation()
			.pipe(map((reservation) => reservation?.id ?? null)),
		{ initialValue: null as number | null },
	);
	readonly allServices = toSignal(
		inject(ReservationSummaryStoreService)
			.getters()
			.offerService()
			.pipe(
				map((services) =>
					(services ?? []).filter(
						(service) =>
							service.serviceType?.id !== ReservationServicesDialogComponent.EXCLUDED_SERVICE_TYPE_ID,
					),
				),
			),
		{ initialValue: [] as OfferService[] },
	);
	private readonly dialogRef = inject(MatDialogRef<ReservationServicesDialogComponent>);
	private readonly reservationSummaryApiService = inject(ReservationSummaryApiService);
	private readonly reservationSummaryStoreService = inject(ReservationSummaryStoreService);
	private readonly infobox = inject(InfoboxService);
	private readonly loader = inject(LoaderService);
	private readonly destroyRef = inject(DestroyRef);

	ngOnInit(): void {
		const { getOfferService } = this.reservationSummaryApiService.actions();
		const queryString = `?${this.data.params.toString()}`;
		getOfferService(queryString);

		this.globalServiceControl.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
			this.selectedGlobalService.set(null);
		});
	}

	participantControlAt(index: number): FormControl<string> {
		return this.participantServiceControls.at(index);
	}

	initialSlotServicesSumAt(index: number): number {
		return getReservationSlotServicesSum(this.data.slots[index]);
	}

	initialSlotPriceAdjustmentAt(index: number): number {
		return this.data.slots[index].priceAdjustment ?? 0;
	}

	onGlobalServiceSelected(event: MatAutocompleteSelectedEvent): void {
		this.selectedGlobalService.set(event.option.value);
	}

	addServiceToAllSlots(): void {
		const selectedServiceName = this.selectedGlobalService();
		if (!selectedServiceName) {
			return;
		}

		const selectedService = findServiceByName({
			allServices: this.allServices(),
			slots: this.slots(),
			serviceName: selectedServiceName,
			mode: 'not-on-all-slots',
		});
		if (!selectedService) {
			return;
		}

		const updatedSlots = this.slots().map((slot) => {
			const hasService = slot.slotServices?.some((slotService) => slotService.service?.id === selectedService.id);
			if (hasService) {
				return slot;
			}

			const updatedSlot = {
				...slot,
				slotServices: [...slot.slotServices, this.createSlotService(selectedService, slot)],
			};
			recalculateReservationSlotServicesPrices(updatedSlot, this.data.nightsStay);
			return updatedSlot;
		});

		this.slots.set(updatedSlots);
		this.globalServiceControl.setValue('');
		this.selectedGlobalService.set(null);
	}

	addServiceToSlot(index: number, selectedServiceName: string): void {
		const selectedService = findServiceByName({
			allServices: this.allServices(),
			slots: this.slots(),
			serviceName: selectedServiceName,
			mode: 'not-on-slot',
			slotIndex: index,
		});
		if (!selectedService) {
			return;
		}

		const updatedSlots = [...this.slots()];
		const updatedSlot = {
			...updatedSlots[index],
			slotServices: [
				...updatedSlots[index].slotServices,
				this.createSlotService(selectedService, updatedSlots[index]),
			],
		};
		recalculateReservationSlotServicesPrices(updatedSlot, this.data.nightsStay);
		updatedSlots[index] = updatedSlot;
		this.slots.set(updatedSlots);
		this.participantControlAt(index).setValue('');
	}

	updateSlotPriceAdjustment(slotIndex: number, priceAdjustment: number): void {
		const updatedSlots = [...this.slots()];
		if ((updatedSlots[slotIndex].priceAdjustment ?? 0) === priceAdjustment) {
			return;
		}

		updatedSlots[slotIndex] = {
			...updatedSlots[slotIndex],
			priceAdjustment,
		};
		this.slots.set(updatedSlots);
	}

	removeServiceFromSlot(slotIndex: number, serviceIndex: number): void {
		const updatedSlots = [...this.slots()];
		const slotServices = [...updatedSlots[slotIndex].slotServices];
		slotServices.splice(serviceIndex, 1);
		const updatedSlot = {
			...updatedSlots[slotIndex],
			slotServices: slotServices,
		};
		recalculateReservationSlotServicesPrices(updatedSlot, this.data.nightsStay);
		updatedSlots[slotIndex] = updatedSlot;
		this.slots.set(updatedSlots);
	}

	closeDialog(): void {
		this.dialogRef.close();
	}

	async saveServices(): Promise<void> {
		const { patchOfferSlot, getReservation } = this.reservationSummaryApiService.actions();

		this.isSaving.set(true);
		this.loader.show();

		try {
			await Promise.all(
				this.slots().map((slot) =>
					patchOfferSlot(slot.id, {
						slotServices: this.buildSlotServicesPayload(slot),
						priceAdjustment: slot.priceAdjustment ?? null,
					}),
				),
			);

			const reservationId = this.reservationId();
			if (reservationId) {
				await getReservation(reservationId);
			}

			this.successfulSaveActions();
		} finally {
			this.isSaving.set(false);
			this.loader.hide();
		}
	}

	private successfulSaveActions(): void {
		this.dialogRef.close(true);
		this.infobox.success(RESERVATION_SERVICES_DIALOG_MESSAGES.saveSuccess);
		this.showPaymentTermModal();
	}

	private showPaymentTermModal(): void {
		const { paymentTerm } = this.reservationSummaryStoreService.getters();
		paymentTerm()
			.pipe(
				take(1),
				filter((terms) => terms.some((term) => term.countKind === 'V')),
			)
			.subscribe(() => {
				this.infobox.warning(RESERVATION_SERVICES_DIALOG_MESSAGES.paymentTermFixedValueWarning);
			});
	}

	private buildSlotServicesPayload(slot: Slot): { service: number }[] {
		return slot.slotServices
			.filter((slotService) => slotService.service?.id != null)
			.map((slotService) => ({ service: slotService.service.id }));
	}

	private createSlotService(service: OfferService, slot: Slot): SlotService {
		return {
			id: 0,
			slot: slot.id,
			price: 0,
			countingMethod: service.countingMethod,
			kinds: [],
			isActive: service.isActive,
			description: service.description,
			code: service.code,
			overwrittenPrice: null,
			count: '',
			method: '',
			methodValue: service.methodValue,
			service: { ...service },
		};
	}
}
